import { motion } from 'framer-motion';

interface StepLayoutProps {
  step: number;
  totalSteps: number;
  question: string;
  description?: string;
  onBack: () => void;
  onNext: () => void;
  canGoNext: boolean;
  children: React.ReactNode;
}

export default function StepLayout({
  step,
  totalSteps,
  question,
  description,
  onBack,
  onNext,
  canGoNext,
  children,
}: StepLayoutProps) {
  const pct = (step / totalSteps) * 100;

  return (
    <div className="min-h-[100dvh] bg-cream flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-cream/90 backdrop-blur-md">
        <div className="flex items-center h-14 px-4 md:px-6 lg:px-8 max-w-[1200px] mx-auto">
          <button
            onClick={onBack}
            className="w-8 h-8 flex items-center justify-center text-navy cursor-pointer active:scale-90 transition-transform"
            aria-label="뒤로가기"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <span className="ml-3 text-xs font-semibold text-navy-light">
            예복 취향 분석 중
          </span>
        </div>

        {/* Progress */}
        <div className="px-4 md:px-6 lg:px-8 pb-4 max-w-[1200px] mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-medium text-navy-light">
              STEP {step} / {totalSteps}
            </span>
            <span className="text-[11px] font-semibold text-navy">
              {Math.round(pct)}%
            </span>
          </div>
          <div className="h-1 bg-cream-dark rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-navy rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            />
          </div>
        </div>
      </div>

      {/* Question */}
      <div className="px-4 md:px-6 lg:px-8 pt-2 pb-6 max-w-[1200px] mx-auto w-full">
        <motion.h2
          key={question}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="font-serif text-xl md:text-2xl text-navy leading-relaxed"
        >
          {question}
        </motion.h2>
        {description && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.4 }}
            className="text-xs md:text-sm text-navy-light mt-2 leading-relaxed"
          >
            {description}
          </motion.p>
        )}
      </div>

      {/* Choices */}
      <div className="flex-1 px-4 md:px-6 lg:px-8 pb-4 overflow-y-auto max-w-[1200px] mx-auto w-full">
        {children}
      </div>

      {/* Bottom CTA */}
      <div className="shrink-0 bg-white border-t border-cream-dark px-4 md:px-6 lg:px-8 py-4">
        <div className="max-w-[1200px] mx-auto">
          <button
            onClick={onNext}
            disabled={!canGoNext}
            className={`w-full font-semibold rounded-xl py-4 text-sm md:text-base transition-all duration-200 whitespace-nowrap cursor-pointer ${
              canGoNext
                ? 'bg-navy hover:bg-navy-light text-white shadow-lg shadow-navy/12'
                : 'bg-cream-dark text-beige-dark cursor-not-allowed'
            }`}
          >
            {step === totalSteps ? '결과 분석하기' : '다음 질문 보기'}
          </button>
        </div>
      </div>
    </div>
  );
}